import { type Payout, PayoutStatus, Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { GridPaginationModel } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { keepPreviousData } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

import { CopyButton } from "~/components/copy_button";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { StatusChip } from "~/components/status_chip";
import { useConfig } from "~/contexts/config";
import { downloadCsv, initStreamingExport } from "~/utilities/csv";
import { Money, renderMoneyWithoutCurrencySymbol } from "~/utilities/money";

const GRID_HEIGHT = 580;

const NOT_AVAILABLE = "N/A";

const EXPORT_PAYOUTS_PAGE_SIZE = 100;

const EXPORT_PAYOUTS_DEFAULT_FILE_NAME = "payouts.csv";

const CSV_HEADERS = [
  "Arrival Date",
  "Payout ID",
  "Status",
  "Destination",
  "Currency",
  "Amount",
];

const payoutStatusLabels: Record<PayoutStatus, string> = {
  [PayoutStatus.Pending]: "Pending",
  [PayoutStatus.Completed]: "Completed",
  [PayoutStatus.Failed]: "Failed",
  [PayoutStatus.Canceled]: "Canceled",
};

const payoutStatusColors: Record<PayoutStatus, "info" | "success" | "error"> = {
  [PayoutStatus.Pending]: "info",
  [PayoutStatus.Completed]: "success",
  [PayoutStatus.Failed]: "error",
  [PayoutStatus.Canceled]: "error",
};

export interface PayoutHistoryProps {
  clientId: string;
}

function formatPayoutDate(date: Date, locale: string, timeZone: string) {
  return date.toLocaleDateString(locale, {
    timeZone,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatExportDate(date: Date, locale: string, timeZone: string) {
  return date.toLocaleDateString(locale, {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getDestination(payout: Payout) {
  if (!payout.last4) {
    return payout.bank_name || NOT_AVAILABLE;
  }

  return payout.bank_name
    ? `${payout.bank_name} ••••${payout.last4}`
    : `••••${payout.last4}`;
}

function getArrivalDate(payout: Payout, formatDate: (date: Date) => string) {
  if (!payout.arrival_date) {
    return NOT_AVAILABLE;
  }

  const hasArrived = payout.status === PayoutStatus.Completed;
  return `${hasArrived ? "" : "Est. "}${formatDate(payout.arrival_date)}`;
}

export function PayoutHistory({ clientId }: PayoutHistoryProps) {
  const { localization } = useConfig();
  const theme = useTheme();
  const navigate = useNavigate({ from: "/clients/$clientId/account" });
  const search = useSearch({ from: "/clients/$clientId/account" });
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const { data: payoutPage, isFetching } =
    Query.useCore_getClientPayoutsByPageQuery(
      clientId,
      search.payoutsPage,
      search.payoutsPageSize,
      { placeholderData: keepPreviousData },
    );

  const payouts = payoutPage?.data ?? [];
  const rowCount = payoutPage?.total_count ?? 0;

  const formatDate = useMemo(
    () => (date: Date) =>
      formatPayoutDate(date, localization.locale, localization.timeZone),
    [localization.locale, localization.timeZone],
  );

  const paginationModel = useMemo(
    () => ({
      page: search.payoutsPage - 1,
      pageSize: search.payoutsPageSize,
    }),
    [search.payoutsPage, search.payoutsPageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      navigate({
        search: (prev) => ({
          ...prev,
          payoutsPage:
            model.pageSize !== prev.payoutsPageSize ? 1 : model.page + 1,
          payoutsPageSize: model.pageSize,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const columns = useMemo<GridColDef<Payout>[]>(
    () => [
      {
        field: "arrival_date",
        headerName: "Arrival Date",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {getArrivalDate(params.row, formatDate)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params.row.status ? (
            <StatusChip
              label={payoutStatusLabels[params.row.status]}
              color={theme.palette[payoutStatusColors[params.row.status]].main}
              dataTestId="payout-history-status-text"
            />
          ) : (
            <Typography
              variant="body2"
              data-testid="payout-history-status-text"
            >
              {NOT_AVAILABLE}
            </Typography>
          ),
      },
      {
        field: "payout_id",
        headerName: "Payout ID",
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ width: "100%" }}
          >
            <Typography
              variant="body2"
              title={params.row.payout_id}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.payout_id}
            </Typography>
            <CopyButton
              value={params.row.payout_id}
              label="Copy payout ID"
              dataTestId="payout-history-id-copy-button"
            />
          </Stack>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: 140,
        flex: 1,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {new Money(params.row.amount, params.row.currency).format()}
          </Typography>
        ),
      },
      {
        field: "destination",
        headerName: "Destination",
        minWidth: 180,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" gap={1}>
            <PublicSvgIcon name="account_balance" size={13} />
            <Typography variant="body2">
              {getDestination(params.row)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "chevron",
        headerName: "",
        width: 44,
        sortable: false,
        align: "right",
        renderCell: () => <PublicSvgIcon name="chevron_right" size={20} />,
      },
    ],
    [formatDate, theme.palette],
  );

  const toExportRow = useCallback(
    (payout: Payout) => [
      payout.arrival_date
        ? formatExportDate(
            payout.arrival_date,
            localization.locale,
            localization.timeZone,
          )
        : NOT_AVAILABLE,
      payout.payout_id,
      payout.status ? payoutStatusLabels[payout.status] : NOT_AVAILABLE,
      getDestination(payout),
      payout.currency,
      renderMoneyWithoutCurrencySymbol(
        payout.amount,
        payout.currency,
        localization.locale,
      ),
    ],
    [localization.locale, localization.timeZone],
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    const buffer: Record<number, unknown[][]> = {};
    const nextToWriteRef = { current: 1 };
    let writable: FileSystemWritableFileStream | undefined;

    try {
      let onPageArrive: (() => Promise<void>) | undefined;
      if ("showSaveFilePicker" in window) {
        const init = await initStreamingExport(
          CSV_HEADERS,
          EXPORT_PAYOUTS_DEFAULT_FILE_NAME,
          buffer,
          nextToWriteRef,
        );
        writable = init.writable;
        onPageArrive = init.onPageArrive;
      }

      const client = Query.Client();
      let page = 1;
      let totalPages = 1;

      do {
        const result = await client.core_getClientPayoutsByPage(
          clientId,
          page,
          EXPORT_PAYOUTS_PAGE_SIZE,
        );
        buffer[page] = result.data.map(toExportRow);
        totalPages = result.total_pages;
        await onPageArrive?.();
        page++;
      } while (page <= totalPages);

      if (onPageArrive) {
        await onPageArrive();
        await writable?.close();
      } else {
        downloadCsv(
          [CSV_HEADERS, ...Object.values(buffer).flat()],
          EXPORT_PAYOUTS_DEFAULT_FILE_NAME,
        );
      }

      enqueueSnackbar("Successfully exported payouts", { variant: "success" });
    } catch (error) {
      const isAbortError =
        error instanceof Error && error.name === "AbortError";
      if (!isAbortError) {
        console.error("Couldn't export payouts:", error);
        enqueueSnackbar("Failed to export payouts", { variant: "error" });
      }
      try {
        await writable?.abort();
      } catch (err) {
        console.error("Couldn't abort writable file stream:", err);
      }
    } finally {
      setIsExporting(false);
    }
  }, [clientId, enqueueSnackbar, toExportRow]);

  return (
    <Stack id="payout_history" spacing={2.5}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="text"
          onClick={() => {
            void handleExport();
          }}
          disabled={rowCount === 0 || isExporting}
          data-testid="payout-history-export-button"
          sx={{ px: 1, py: 0.75, borderRadius: 1 }}
          startIcon={
            <PublicSvgIcon
              name="ios_share"
              inheritColor
              sx={{ "& svg": { width: 12, height: 16 } }}
            />
          }
        >
          {isExporting ? "Exporting…" : "Export"}
        </Button>
      </Stack>

      <Typography variant="h5SolidDivider" component="div" sx={{ p: 0 }}>
        <Box
          component="button"
          type="button"
          aria-expanded={isOpen}
          aria-controls="payout_history-content"
          onClick={() => setIsOpen((open) => !open)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            width: "100%",
            p: 0,
            pb: 1.5,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            color: "text.primary",
          }}
          data-testid="payouts-toggle-button"
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <PublicSvgIcon name="account_balance" />
            <Typography
              variant="inherit"
              component="span"
              sx={{ color: "text.primary" }}
            >
              Payout History
            </Typography>
          </Box>
          <PublicSvgIcon
            name="expand_less"
            inheritColor
            sx={{
              display: "flex",
              transform: isOpen ? "none" : "rotate(180deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>
      </Typography>

      <Collapse in={isOpen} id="payout_history-content" unmountOnExit>
        <Box
          sx={{
            height: GRID_HEIGHT,
            backgroundColor: "common.white",
            borderRadius: 1.5,
          }}
        >
          <ReadableDataGrid<Payout>
            dataTestId="payout-history-grid"
            storageKey="PayoutHistory"
            columns={columns}
            rows={payouts}
            rowCount={rowCount}
            getRowId={(row) => row.payout_id}
            loading={isFetching}
            hideToolbar
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 50, 100]}
            rowNavigation={{
              getUrl: (row: Payout) =>
                `/clients/${row.client_id ?? clientId}/payouts/${row.payout_id}`,
              onNavigate: (url: string) => navigate({ to: url }),
            }}
            localeText={{
              noRowsLabel: "No Payouts",
            }}
          />
        </Box>
      </Collapse>
    </Stack>
  );
}
